import axios from 'axios'
import React, { useState, useContext, useEffect } from 'react'
import dayjs from 'dayjs'

import { baseUrl } from '../../../baseUrl';
import ConfirmDialog from '../../../components/ConfirmDialog';
import AlertDialog from '../../../components/AlertDialog';
import { UserContext } from '../../../Contexts/UserContext';
import { generateAndDownloadFullReliefVoucher } from './generateAndDownloadFullReliefVoucher';

function PartiallyRelievedInmates() {
    const { setLoading, user } = useContext(UserContext);

    const [partiallyRelievedInmates, setPartiallyRelievedInmates] = useState([]);
    const [filteredInmates, setFilteredInmates] = useState([]);
    const [selectedInmate, setSelectedInmate] = useState(null);
    const [open1, setOpen1] = useState(false);
    const [open2, setOpen2] = useState(false);
    const [modalText, setModalText] = useState("");
    const [modalHeading, setModalHeading] = useState("");
    const [hostelFilter, setHostelFilter] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');

    const fetchPartiallyRelievedInmates = (hostel = 'all') => {
        setLoading(true);

        const url = hostel === 'all'
            ? `${baseUrl}/clerk-a3/get-partially-relieved-inmates`
            : `${baseUrl}/clerk-a3/get-partially-relieved-inmates?hostel=${hostel}`;

        axios.get(url)
            .then((res) => {
                setPartiallyRelievedInmates(res.data);
                applyFilters(res.data, hostel, searchQuery);
                setLoading(false);
            })
            .catch(err => {
                console.log('Error fetching partially relieved inmates:', err);
                setLoading(false);
                setModalHeading("Error");
                setModalText("Error fetching partially relieved inmates");
                setOpen1(true);
            });
    };

    const applyFilters = (inmates, hostelFilter, searchQuery) => {
        let filtered = inmates;

        // Apply search filter
        if (searchQuery.trim() !== '') {
            filtered = filtered.filter(inmate =>
                inmate.hostel_admission_no.toLowerCase().includes(searchQuery.toLowerCase())
            );
        }

        setFilteredInmates(filtered);
    };

    const handleHostelFilterChange = (e) => {
        const selectedHostel = e.target.value;
        setHostelFilter(selectedHostel);
        fetchPartiallyRelievedInmates(selectedHostel);
    };

    const handleSearchChange = (e) => {
        const query = e.target.value;
        setSearchQuery(query);
        applyFilters(partiallyRelievedInmates, hostelFilter, query);
    };

    const clearSearch = () => {
        setSearchQuery('');
        applyFilters(partiallyRelievedInmates, hostelFilter, '');
    };

    const handleFullRelief = (inmate) => {
        setSelectedInmate(inmate);
        const balanceAmount = inmate.advance_amount - (inmate.used_amount || 0);
        let settlementText = "";

        if (balanceAmount > 0) {
            settlementText = `Amount to be refunded: Rs.${balanceAmount}`;
        } else if (balanceAmount < 0) {
            settlementText = `Amount to be collected: Rs.${Math.abs(balanceAmount)}`;
        } else {
            settlementText = "No amount due - Settlement complete";
        }

        setModalHeading("Confirm Full Relief");
        setModalText(`You are about to fully relieve:
        Name: ${inmate.name}
        Hostel Admission No: ${inmate.hostel_admission_no}
        College Admission No: ${inmate.admission_no}
        Hostel: ${inmate.hostel || 'N/A'}
        Block: ${inmate.block_name || 'N/A'}
        Room: ${inmate.room_no || 'N/A'}
        Advance Paid: Rs.${inmate.advance_amount}
        Used Amount: Rs.${inmate.used_amount || 0}
        ${settlementText}
        Vacating Date: ${dayjs(inmate.vacating_date).format('DD/MM/YYYY')}
        
        This will complete the relief process and generate a settlement voucher.
        Do you want to confirm?`);
        setOpen2(true);
    };

    const confirmFullRelief = () => {
        if (!selectedInmate) return;

        setLoading(true);


        const advanceAmount = selectedInmate.advance_amount || 0;
        const usedAmount = selectedInmate.used_amount || 0;
        const balanceAmount = advanceAmount - usedAmount;
        const isRefund = balanceAmount > 0;
        axios.post(`${baseUrl}/clerk-a3/complete-full-relief`, {
            hostelAdmissionNo: selectedInmate.hostel_admission_no,
            joiningDate: selectedInmate.joining_date,
            vacatingDate: selectedInmate.vacating_date,
            programme: selectedInmate.programme,
            department: selectedInmate.department,
            hostel: selectedInmate.hostel,
            isRefund
        }).then((res) => {
            // Generate the voucher after successful relief
            const receiptOrVoucherNo = res.data.receiptOrVoucherNo
            generateAndDownloadFullReliefVoucher(selectedInmate, receiptOrVoucherNo);
            setModalHeading("Success");
            setModalText(`Inmate ${selectedInmate.hostel_admission_no} has been fully relieved. Settlement voucher has been generated.`);
            setLoading(false);
            setOpen1(true);
            fetchPartiallyRelievedInmates(hostelFilter); // Refresh the list with current filter
        }).catch(err => {
            console.log('Error in full relief:', err);
            setLoading(false);
            setModalHeading("Error");
            setModalText(err.response?.data?.msg || "Error processing full relief");
            setOpen1(true);
        });
    };

    // Function to generate voucher without processing relief (for preview)
    const generateVoucherPreview = (inmate) => {
        generateAndDownloadFullReliefVoucher(inmate);
    };

    useEffect(() => {
        fetchPartiallyRelievedInmates();
    }, []);

    return (
        <div className="w-full p-4">
            <div className="m-2 p-2 grid grid-cols-2 w-200 gap-4 mb-3">
                <label className="font-bold" htmlFor="">Hostel Admission No :</label>{" "}
                <input
                    type="text"
                    placeholder="Search by Hostel Admission No."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    className="w-11/12 py-2 px-3 rounded-xl ring-2 ring-slate-300 focus:outline-none"
                />
                {searchQuery && (
                    <button
                        onClick={clearSearch}
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        title="Clear search"
                    >
                        ×
                    </button>
                )}
            </div>
            <div className="flex items-center justify-between my-5">
                {/* Search Filter */}


                {/* Hostel Filter Dropdown */}
                <div className="flex items-center gap-2">
                    <label className="text-sm font-medium text-gray-700">Filter by Hostel:</label>
                    <select
                        value={hostelFilter}
                        onChange={handleHostelFilterChange}
                        className="px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="all">All Hostels</option>
                        <option value="MH">Men's Hostel (MH)</option>
                        <option value="LH">Ladies' Hostel (LH)</option>
                    </select>
                </div>
            </div>

            {/* Results Summary */}
            {searchQuery && (
                <div className="mb-3 text-sm text-gray-600">
                    Showing {filteredInmates.length} result(s) for "{searchQuery}"
                    {hostelFilter !== 'all' && ` in ${hostelFilter === 'MH' ? "Men's Hostel" : "Ladies' Hostel"}`}
                </div>
            )}

            {filteredInmates.length === 0 ? (
                <div className="text-center py-8">
                    <p className="text-gray-500">
                        {searchQuery
                            ? `No inmates found matching "${searchQuery}"`
                            : hostelFilter === 'all'
                                ? "No partially relieved inmates found."
                                : `No partially relieved inmates found for ${hostelFilter === 'MH' ? "Men's Hostel" : "Ladies' Hostel"}.`
                        }
                    </p>
                    {searchQuery && (
                        <button
                            onClick={clearSearch}
                            className="mt-2 text-blue-600 hover:text-blue-800 text-sm"
                        >
                            Clear search to see all results
                        </button>
                    )}
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="min-w-full bg-white border border-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-2 border-b text-left">Hostel Admission No</th>
                                <th className="px-4 py-2 border-b text-left">Name</th>
                                <th className="px-4 py-2 border-b text-left">College Admission No</th>
                                <th className="px-4 py-2 border-b text-left">Programme</th>
                                <th className="px-4 py-2 border-b text-left">Department</th>
                                <th className="px-4 py-2 border-b text-left">Vacating Date</th>
                                <th className="px-4 py-2 border-b text-left">Advance Paid</th>
                                <th className="px-4 py-2 border-b text-left">Used Amount</th>
                                <th className="px-4 py-2 border-b text-left">Balance Amount</th>
                                <th className="px-4 py-2 border-b text-left">Partial Relief Date</th>
                                <th className="px-4 py-2 border-b text-left">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredInmates.map((inmate, index) => {
                                const balanceAmount = inmate.advance_amount - (inmate.used_amount || 0);
                                return (
                                    <tr key={index} className="hover:bg-gray-50">
                                        <td className="px-4 py-2 border-b">{inmate.hostel_admission_no}</td>
                                        <td className="px-4 py-2 border-b">{inmate.name}</td>
                                        <td className="px-4 py-2 border-b">{inmate.admission_no}</td>
                                        <td className="px-4 py-2 border-b">{inmate.programme}</td>
                                        <td className="px-4 py-2 border-b">{inmate.department}</td>
                                        <td className="px-4 py-2 border-b">
                                            {dayjs(inmate.vacating_date).format('DD/MM/YYYY')}
                                        </td>
                                        <td className="px-4 py-2 border-b font-semibol">
                                            Rs.{inmate.advance_amount}
                                        </td>
                                        <td className="px-4 py-2 border-b">
                                            Rs.{inmate.used_amount || 0}
                                        </td>
                                        <td className={`px-4 py-2 border-b font-semibold ${balanceAmount > 0 ? 'text-green-600' :
                                            balanceAmount < 0 ? 'text-red-600' : 'text-gray-600'
                                            }`}>
                                            Rs.{balanceAmount}
                                            {balanceAmount > 0 && <span className="text-xs block">(Refund)</span>}
                                            {balanceAmount < 0 && <span className="text-xs block">(Due)</span>}
                                        </td>
                                        <td className="px-4 py-2 border-b">
                                            {dayjs(inmate.partial_relief_date).format('DD/MM/YYYY')}
                                        </td>
                                        <td className="px-4 py-2 border-b">
                                            <div className="flex flex-col gap-2">
                                                <button
                                                    onClick={() => handleFullRelief(inmate)}
                                                    className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700 transition-colors"
                                                >
                                                    Full Relief
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            <AlertDialog
                open={open1}
                setOpen={setOpen1}
                modalHeading={modalHeading}
                modalText={modalText}
            />
            <ConfirmDialog
                open={open2}
                setOpen={setOpen2}
                modalHeading={modalHeading}
                modalText={modalText}
                confirmFunction={confirmFullRelief}
            />
        </div>
    );
}

export default PartiallyRelievedInmates;